import React, { useMemo } from 'react';
import { Button } from '@patternfly/react-core';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { formatTimezoneAndDateTime } from 'services/UtilityService';
import { withErrorBoundary } from 'react-error-boundary';
import ComponentError from 'components/Error/ComponentError';
import { handleError } from 'services/ErrorService';
import classNames from 'classnames';

import './styles.scss';

type ItemProps = {
  id: string;
  title: string;
  startDate?: DateTime;
  endDate?: DateTime;
  timezone?: string;
  onView: (id: string) => void;
  buttonLabel?: string;
  isOngoing?: boolean;
  dataAnalyticsTag?: string;
};

const UpcomingEventItem = ({
  id,
  title,
  startDate,
  endDate,
  timezone,
  onView,
  buttonLabel: buttonTitle,
  isOngoing,
  dataAnalyticsTag,
}: ItemProps) => {
  const { t } = useTranslation();
  const containerClassnames = classNames('upcoming-events__evt', {
    'upcoming-events__evt--ongoing': isOngoing,
  });

  const titleClassnames = classNames('upcoming-events__evt-title', {
    'upcoming-events__evt-title--ongoing': isOngoing,
  });

  const { formattedStartDate, formattedEndDate } = useMemo(() => {
    if (isOngoing) {
      return {
        formattedStartDate: null,
        formattedEndDate: endDate
          ? formatTimezoneAndDateTime(endDate.toISO(), timezone)
          : null,
      };
    }

    return {
      formattedStartDate: startDate
        ? formatTimezoneAndDateTime(startDate.toISO(), timezone)
        : null,
      formattedEndDate: null,
    };
  }, [startDate, endDate, timezone, isOngoing]);

  return (
    <div className={containerClassnames} key={id}>
      {isOngoing ? (
        <div
          data-testid="upcoming-events__status"
          className="upcoming-events__evt-status"
        />
      ) : null}
      <p className={titleClassnames}>
        <span>{title}</span>
      </p>
      {formattedStartDate && (
        <p className="upcoming-events__evt-date">
          {t('Start: {{formattedStartDate}}', { formattedStartDate })}
        </p>
      )}
      {formattedEndDate && (
        <p className="upcoming-events__evt-date">
          {t('End: {{formattedEndDate}}', { formattedEndDate })}
        </p>
      )}
      <Button
        className="upcoming-events__evt-btn"
        variant="link"
        size="sm"
        title={buttonTitle}
        onClick={() => onView(id)}
        data-analytics-id={`${dataAnalyticsTag}-btn-my-activity-page-lp`}
      >
        {buttonTitle}
      </Button>
    </div>
  );
};

export default withErrorBoundary(
  UpcomingEventItem,
  ComponentError,
  handleError,
);
